import React, { useState, useEffect, useRef } from 'react';
import { Chart } from 'primereact/chart';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton, SelectButtonChangeEvent } from 'primereact/selectbutton';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import MonthlyTrendChart from './MonthlyTrendChart';
import {
  PredictionAccuracy,
  LeagueOption,
  CountryOption,
  SeasonOption,
  PredictionTypeOption,
  SelectOption,
  ChartDataItem,
  ChartData,
  ChartOptions,
  DataLabelsContext,
} from './types';
import { TailSpin } from 'react-loader-spinner';
import { FaChartLine } from 'react-icons/fa';
import { t } from 'i18next';

const PredictionAccuracyCharts: React.FC = () => {
  const [data, setData] = useState<PredictionAccuracy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const dt = useRef<any>(null);

  const [allLeagues, setAllLeagues] = useState<LeagueOption[]>([]);
  const [allCountries, setAllCountries] = useState<CountryOption[]>([]);
  const [allSeasons, setAllSeasons] = useState<SeasonOption[]>([]);
  const [allPredictionTypes, setAllPredictionTypes] = useState<
    PredictionTypeOption[]
  >([]);

  const [selectedLeague, setSelectedLeague] = useState<LeagueOption | null>(
    null,
  );
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(
    null,
  );
  const [selectedSeason, setSelectedSeason] = useState<SeasonOption | null>(
    null,
  );
  const [selectedPredictionType, setSelectedPredictionType] =
    useState<PredictionTypeOption | null>(null);

  const [leagueParam, setLeagueParam] = useState<string | null>(null);
  const [countryParam, setCountryParam] = useState<string | null>(null);
  const [seasonParam, setSeasonParam] = useState<string | null>(null);
  const [predictionTypeIdParam, setPredictionTypeIdParam] = useState<
    number | null
  >(null);

  const [chartType, setChartType] = useState<string>('bar');

  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [chartOptions, setChartOptions] = useState<ChartOptions | null>(null);

  const [showMonthlyTrend, setShowMonthlyTrend] = useState<boolean>(false);

  const chartTypeOptions: SelectOption[] = [{ label: 'Bars', value: 'bar' }];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [leagueParam, countryParam, seasonParam, predictionTypeIdParam]);

  useEffect(() => {
    if (data.length > 0) {
      prepareChartData();
    }
  }, [data, chartType, isMobile]);

  useEffect(() => {
    if (chartData) {
      customizeChartLabels();
    }
  }, [chartData]);

  const customizeChartLabels = () => {
    setTimeout(() => {
      const charts = document.querySelectorAll('.p-chart');
      charts.forEach((chart) => {
        try {
          const chartInstance = (chart as any).__chartjs__;
          if (chartInstance && chartInstance.options) {
            if (
              chartInstance.options.plugins &&
              chartInstance.options.plugins.legend &&
              chartInstance.options.plugins.legend.labels
            ) {
              chartInstance.options.plugins.legend.labels.color = '#FFFFFF';

              if (isMobile) {
                chartInstance.options.plugins.legend.labels.font = {
                  size: 10,
                };
                chartInstance.options.plugins.legend.labels.boxWidth = 15;
              }
            }

            if (chartInstance.options.scales) {
              if (
                chartInstance.options.scales.y &&
                chartInstance.options.scales.y.title
              ) {
                chartInstance.options.scales.y.title.color = '#FFFFFF';
                chartInstance.options.scales.y.ticks =
                  chartInstance.options.scales.y.ticks || {};
                chartInstance.options.scales.y.ticks.color = '#FFFFFF';

                if (isMobile) {
                  chartInstance.options.scales.y.ticks.font = { size: 9 };
                  chartInstance.options.scales.y.title.font = { size: 10 };
                }
              }

              if (
                chartInstance.options.scales.x &&
                chartInstance.options.scales.x.title
              ) {
                chartInstance.options.scales.x.title.color = '#FFFFFF';
                chartInstance.options.scales.x.ticks =
                  chartInstance.options.scales.x.ticks || {};
                chartInstance.options.scales.x.ticks.color = '#FFFFFF';

                if (isMobile) {
                  chartInstance.options.scales.x.ticks.font = { size: 9 };
                  chartInstance.options.scales.x.title.font = { size: 10 };

                  if (chartType === 'bar') {
                    chartInstance.options.scales.x.ticks.maxRotation = isMobile
                      ? 75
                      : 45;
                    chartInstance.options.scales.x.ticks.minRotation = isMobile
                      ? 75
                      : 45;
                  }
                }
              }
            }

            chartInstance.update();
          }
        } catch (e) {
          console.warn('Unable to customize chart colors:', e);
        }
      });
    }, 100);
  };

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      const startTime = Date.now();

      const apiUrl = 'https://betzen.app/api';
      // const apiUrl = 'https://localhost:7014/api';
      let url = `${apiUrl}/predictionaccuracy`;
      const params = new URLSearchParams();

      if (leagueParam) {
        params.append('league', leagueParam);
      }

      if (countryParam) {
        params.append('country', countryParam);
      }

      if (seasonParam) {
        params.append('seasonYear', seasonParam);
      }

      if (predictionTypeIdParam !== null && predictionTypeIdParam !== 0) {
        params.append('predictionTypeId', predictionTypeIdParam.toString());
      }

      console.log('Parameters being sent:', params.toString());

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      console.log('Final URL:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const responseData: PredictionAccuracy[] = await response.json();

      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 400 - elapsedTime);

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      setData(responseData);

      const uniqueLeaguesSet = new Set(responseData.map((item) => item.league));
      const uniqueLeagues: LeagueOption[] = Array.from(uniqueLeaguesSet)
        .filter((league) => league)
        .map((league) => ({
          label: league,
          value: league,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      if (allLeagues.length === 0 || uniqueLeagues.length > allLeagues.length) {
        setAllLeagues(uniqueLeagues);
      }

      const uniqueCountriesSet = new Set(
        responseData.map((item) => item.country),
      );
      const uniqueCountries: CountryOption[] = Array.from(uniqueCountriesSet)
        .filter((country) => country)
        .map((country) => ({
          label: country,
          value: country,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      if (
        allCountries.length === 0 ||
        uniqueCountries.length > allCountries.length
      ) {
        setAllCountries(uniqueCountries);
      }

      const uniqueSeasonsSet = new Set(
        responseData.map((item) => item.seasonYear),
      );
      const uniqueSeasons: SeasonOption[] = Array.from(uniqueSeasonsSet)
        .filter((season) => season)
        .map((season) => ({
          label: season,
          value: season,
        }))
        .sort((a, b) => b.label.localeCompare(a.label));

      if (allSeasons.length === 0 || uniqueSeasons.length > allSeasons.length) {
        setAllSeasons(uniqueSeasons);
      }

      const uniquePredictionTypes: PredictionTypeOption[] = [];
      const predTypeSet = new Set<number>();

      uniquePredictionTypes.push({
        label: 'All Types',
        value: 'All Types',
        id: 0,
      });

      responseData.forEach((item) => {
        if (item.predictionType && !predTypeSet.has(item.predictionTypeId)) {
          predTypeSet.add(item.predictionTypeId);
          uniquePredictionTypes.push({
            label: item.predictionType,
            value: item.predictionType,
            id: item.predictionTypeId,
          });
        }
      });

      const sortedPredictionTypes = [
        uniquePredictionTypes[0],
        ...uniquePredictionTypes
          .slice(1)
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];

      setAllPredictionTypes(sortedPredictionTypes);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching prediction accuracy data:', err);
      setError('Error loading data. Please try again later.');
      setLoading(false);
    }
  };

  const prepareChartData = (): void => {
    if (!data || data.length === 0) return;

    let processedData: ChartDataItem[] = [];

    if (selectedPredictionType && selectedPredictionType.id !== 0) {
      processedData = data.map((item) => ({
        name: item.league || 'Unknown',
        accuracy: parseFloat(item.accuracyPercentage.toFixed(2)),
        total: item.totalPredictions,
        id: item.id,
      }));
    } else if (selectedLeague) {
      processedData = data.map((item) => ({
        name: item.predictionType || 'Unknown',
        accuracy: parseFloat(item.accuracyPercentage.toFixed(2)),
        total: item.totalPredictions,
        id: item.id,
      }));
    } else {
      const grouped: Record<
        string,
        {
          total: number;
          correct: number;
          id: number;
        }
      > = {};

      data.forEach((item) => {
        const key = item.predictionType || 'Unknown';

        if (!grouped[key]) {
          grouped[key] = {
            total: 0,
            correct: 0,
            id: item.predictionTypeId,
          };
        }

        grouped[key].total += item.totalPredictions;
        grouped[key].correct += item.correctPredictions;
      });

      processedData = Object.keys(grouped).map((key) => ({
        name: key,
        accuracy: parseFloat(
          ((grouped[key].correct * 100) / grouped[key].total).toFixed(2),
        ),
        total: grouped[key].total,
        id: grouped[key].id,
      }));
    }

    if (chartType === 'bar' || chartType === 'line') {
      let displayData = processedData;
      let labelFontSize = isMobile ? 9 : 12;

      const chartData: ChartData = {
        labels: displayData.map((item) => {
          let label = item.name;
          if (label.toLowerCase().includes('multigoal')) {
            label = label.replace(/multigoal/gi, 'Multi');
          }
          return label;
        }),
        datasets: [
          {
            label: 'Accuracy (%)',
            data: displayData.map((item) => item.accuracy),
            backgroundColor: displayData.map(() => 'rgba(54, 162, 235, 0.6)'),
            borderColor: 'rgb(54, 162, 235)',
            borderWidth: 1,
            fill: chartType === 'line',
            tension: chartType === 'line' ? 0.4 : 0,
          },
        ],
      };

      const chartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: 'rgba(0, 0, 0, 0.8)',
            borderWidth: 0,
            displayColors: false,
            callbacks: {
              title: function (context: any) {
                return context[0].label;
              },
              afterLabel: function (context: any) {
                const dataIndex = context.dataIndex;
                return `Total Predictions: ${displayData[dataIndex].total}`;
              },
            },
            bodyFont: {
              size: isMobile ? 11 : 14,
            },
            titleFont: {
              size: isMobile ? 12 : 16,
              weight: 'bold',
            },
            padding: 10,
            boxWidth: isMobile ? 200 : 250,
            boxPadding: 6,
          },
          legend: {
            position: 'top',
            labels: {
              font: { size: isMobile ? 10 : 14 },
              color: '#FFFFFF',
              boxWidth: isMobile ? 10 : 40,
            },
          },
          datalabels: {
            anchor: 'center',
            align: 'center',
            rotation: isMobile ? 90 : 0,
            formatter: function (value: any, context: DataLabelsContext) {
              let name = context.chart.data.labels[context.dataIndex];
              if (isMobile) {
                return `${value}%`;
              } else {
                if (
                  typeof name === 'string' &&
                  name.toLowerCase().includes('multigoal')
                ) {
                  name = name.replace(/multigoal/gi, 'multi');
                }
                const displayName =
                  name.length > 15 ? name.substring(0, 13) + '...' : name;
                return [`${displayName}`, `${value}%`];
              }
            },
            color: '#fff',
            font: {
              weight: 'bold',
              size: isMobile ? 10 : 11,
            },
            display: function (context: any) {
              const value = context.dataset.data[context.dataIndex];
              return true;
            },
            textAlign: 'center',
            textStrokeColor: 'rgba(0, 0, 0, 0.5)',
            textStrokeWidth: 1,
            textShadowBlur: 5,
            textShadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        scales: {
          y: {
            min: 0,
            max: 100,
            title: {
              display: true,
              text: isMobile ? 'Acc. (%)' : 'Accuracy (%)',
              color: '#FFFFFF',
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              color: '#FFFFFF',
              stepSize: 20,
              font: {
                size: isMobile ? 9 : 11,
              },
            },
          },
          x: {
            title: {
              display: !isMobile,
              text:
                selectedPredictionType && selectedPredictionType.id !== 0
                  ? 'League'
                  : 'Prediction Type',
              color: '#FFFFFF',
              font: {
                size: isMobile ? 10 : 12,
              },
            },
            ticks: {
              color: '#FFFFFF',
              font: {
                size: labelFontSize,
              },
              maxRotation: isMobile ? 75 : 45,
              minRotation: isMobile ? 75 : 45,
              autoSkip: false,
              padding: 5,
            },
          },
        },
      };

      setChartData(chartData);
      setChartOptions(chartOptions);
    } else if (chartType === 'pie') {
      const backgroundColors = [
        '#42A5F5',
        '#66BB6A',
        '#FFA726',
        '#26C6DA',
        '#7E57C2',
        '#EC407A',
        '#AB47BC',
        '#5C6BC0',
        '#29B6F6',
        '#26A69A',
      ];

      let displayData = processedData;
      if (isMobile) {
        displayData = [...processedData]
          .sort((a, b) => b.accuracy - a.accuracy)
          .slice(0, 5)
          .map((item) => ({
            ...item,
            name:
              item.name.length > 10
                ? item.name.substring(0, 8) + '...'
                : item.name,
          }));
      }

      const chartData: ChartData = {
        labels: displayData.map((item) => {
          let label = item.name;
          if (label.toLowerCase().includes('multigoal')) {
            label = label.replace(/multigoal/gi, 'multi');
          }
          return label;
        }),
        datasets: [
          {
            data: displayData.map((item) => item.accuracy),
            backgroundColor: displayData.map(
              (_, index) => backgroundColors[index % backgroundColors.length],
            ),
            hoverBackgroundColor: displayData.map((_, index) =>
              backgroundColors[index % backgroundColors.length]
                .replace(')', ', 0.8)')
                .replace('rgb', 'rgba'),
            ),
          },
        ],
      };

      const chartOptions: ChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: isMobile ? 'bottom' : 'right',
            labels: {
              font: {
                size: isMobile ? 10 : 14,
              },
              boxWidth: isMobile ? 10 : 40,
            },
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: function (context: any) {
                const label = context.label || '';
                const value = context.raw || 0;
                const total = displayData[context.dataIndex].total;
                return [`${label}: ${value}%`, `Total Predictions: ${total}`];
              },
            },
          },
        },
      };

      setChartData(chartData);
      setChartOptions(chartOptions);
    }
  };

  const handleLeagueChange = (e: DropdownChangeEvent): void => {
    if (e.value) {
      const selectedOption = allLeagues.find(
        (option) => option.value === e.value,
      );
      if (selectedOption) {
        setSelectedLeague(selectedOption);
        setLeagueParam(selectedOption.value);
      }
    } else {
      setSelectedLeague(null);
      setLeagueParam(null);
    }
  };

  const handleCountryChange = (e: DropdownChangeEvent): void => {
    if (e.value) {
      const selectedOption = allCountries.find(
        (option) => option.value === e.value,
      );
      if (selectedOption) {
        setSelectedCountry(selectedOption);
        setCountryParam(selectedOption.value);
      }
    } else {
      setSelectedCountry(null);
      setCountryParam(null);
    }
  };

  const handleSeasonChange = (e: DropdownChangeEvent): void => {
    if (e.value) {
      const selectedOption = allSeasons.find(
        (option) => option.value === e.value,
      );
      if (selectedOption) {
        setSelectedSeason(selectedOption);
        setSeasonParam(selectedOption.value);
      }
    } else {
      setSelectedSeason(null);
      setSeasonParam(null);
    }
  };

  const handlePredictionTypeChange = (e: DropdownChangeEvent): void => {
    if (e.value) {
      const selectedOption = allPredictionTypes.find(
        (option) => option.id === e.value || option.value === e.value,
      );
      if (selectedOption) {
        setSelectedPredictionType(selectedOption);
        setPredictionTypeIdParam(selectedOption.id);

        if (showMonthlyTrend && selectedOption.id === 0) {
          setShowMonthlyTrend(false);
        }
      }
    } else {
      setSelectedPredictionType(null);
      setPredictionTypeIdParam(null);
      if (showMonthlyTrend) {
        setShowMonthlyTrend(false);
      }
    }
  };

  const handleChartTypeChange = (e: SelectButtonChangeEvent): void => {
    setChartType(e.value);
  };

  const handleMonthlyTrendChange = (checked: boolean): void => {
    setShowMonthlyTrend(checked);
  };

  const resetFilters = () => {
    setSelectedLeague(null);
    setSelectedCountry(null);
    setSelectedSeason(null);
    setSelectedPredictionType(null);
    setLeagueParam(null);
    setCountryParam(null);
    setSeasonParam(null);
    setPredictionTypeIdParam(null);
    setShowMonthlyTrend(false);

    if (dt.current) {
      dt.current.reset();
    }
  };

  const accuracyBodyTemplate = (rowData: PredictionAccuracy): JSX.Element => {
    const accuracy = parseFloat(rowData.accuracyPercentage.toFixed(2));
    let bgClass = 'bg-red-100 text-red-800';

    if (rowData.accuracyPercentage >= 80) {
      bgClass = 'bg-green-100 text-green-800';
    } else if (rowData.accuracyPercentage >= 60) {
      bgClass = 'bg-blue-100 text-blue-800';
    } else if (rowData.accuracyPercentage >= 40) {
      bgClass = 'bg-yellow-100 text-yellow-800';
    }

    return (
      <span className={`px-2 py-1 rounded-md ${bgClass}`}>{accuracy}%</span>
    );
  };

  const header = (
    <div className="text-white py-1 px-2">
      <h3 className="text-xl md:text-1xl font-bold mb-1 text-center flex items-center justify-center">
        <div className="mr-2 text-teal-400" />
        Statistics Detail
      </h3>
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm z-50 flex justify-center items-center">
        <div className="glass-card p-8 rounded-2xl flex flex-col items-center">
          <TailSpin color="#5eead4" height={50} width={50} />
          <p className="mt-4 text-teal-300 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="glass-card p-8 rounded-2xl text-center max-w-md">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-red-400 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const isMonthlyTrendDisabled =
    !selectedPredictionType || selectedPredictionType.id === 0;

  return (
    <div className="py-14 pt-18 px-5 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto prediction-accuracy-container">
        <div className="text-center mb-10">
          <h1 className="text-2xl sm:text-4xl font-extrabold mb-3 text-white tracking-tight flex items-center justify-center gap-3 sm:gap-4">
            <span className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-indigo-600/30 to-purple-600/30 backdrop-blur-sm border border-white/10 shadow-lg flex-shrink-0">
              <FaChartLine className="text-lg sm:text-xl text-teal-400" />
            </span>
            {t('predictionAccuracy.title')}
          </h1>
          <p className="text-gray-400 max-w-xl mx-auto text-sm sm:text-base">
            {t('predictionAccuracy.subtitle')}
          </p>
        </div>

        <div className="glass-card p-4 sm:p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('predictionAccuracy.country')}
              </label>
              <Dropdown
                value={selectedCountry ? selectedCountry.value : null}
                options={allCountries}
                onChange={handleCountryChange}
                placeholder={t('predictionAccuracy.allCountries') as string}
                className="w-full h-9 md:h-10"
                optionLabel="label"
                filter
                showClear
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('predictionAccuracy.league')}
              </label>
              <Dropdown
                value={selectedLeague ? selectedLeague.value : null}
                options={allLeagues}
                onChange={handleLeagueChange}
                placeholder={t('predictionAccuracy.allLeagues') as string}
                className="w-full h-9 md:h-10"
                optionLabel="label"
                filter
                showClear
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('predictionAccuracy.season')}
              </label>
              <Dropdown
                value={selectedSeason ? selectedSeason.value : null}
                options={allSeasons}
                onChange={handleSeasonChange}
                placeholder={t('predictionAccuracy.allSeasons') as string}
                className="w-full h-9 md:h-10"
                optionLabel="label"
                showClear
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('predictionAccuracy.predictionType')}
              </label>
              <Dropdown
                value={
                  selectedPredictionType ? selectedPredictionType.id : null
                }
                options={allPredictionTypes}
                onChange={handlePredictionTypeChange}
                placeholder={t('predictionAccuracy.allTypes') as string}
                className="w-full h-9 md:h-10"
                optionLabel="label"
                optionValue="id"
                filter
                showClear
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div className="flex items-center">
              <span className="text-sm mr-3">
                {t('predictionAccuracy.monthlyTrend')}:
              </span>
              <InputSwitch
                checked={showMonthlyTrend}
                onChange={(e) => handleMonthlyTrendChange(e.value as boolean)}
                disabled={isMonthlyTrendDisabled}
                className="mr-3"
              />
              {isMonthlyTrendDisabled && (
                <small className="text-gray-300">
                  {t('predictionAccuracy.selectSpecificType')}
                </small>
              )}
            </div>

            <Button
              icon="pi pi-filter-slash"
              label={t('predictionAccuracy.resetFilters') as string}
              className="mt-2 sm:mt-0 p-button-sm p-button-outlined"
              onClick={resetFilters}
            />
          </div>
        </div>

        {showMonthlyTrend && (
          <MonthlyTrendChart
            predictionTypeId={selectedPredictionType?.id || 0}
            league={leagueParam}
            country={countryParam}
            seasonYear={seasonParam}
          />
        )}

        {!showMonthlyTrend && (
          <div className="glass-card p-4 sm:p-6 mb-6">
            <h3 className="text-xl md:text-1xl font-bold mb-3 md:mb-6 text-center">
              {selectedPredictionType && selectedPredictionType.id !== 0
                ? `${t('predictionAccuracy.accuracyFor')} ${selectedPredictionType.label}`
                : selectedLeague
                  ? `${t('predictionAccuracy.predictionAccuracyFor')} ${selectedLeague.label}`
                  : t('predictionAccuracy.accuracyByType')}
            </h3>

            {!chartData ? (
              <div className="text-center p-4 md:p-8 text-gray-500">
                {t('predictionAccuracy.noDataForFilters')}
              </div>
            ) : (
              <div
                className="w-full"
                style={{ height: isMobile ? '300px' : '400px' }}
              >
                <Chart
                  type={chartType}
                  data={chartData}
                  options={chartOptions || undefined}
                  style={{ height: '100%' }}
                  plugins={[
                    {
                      id: 'chartjs-plugin-datalabels',
                      beforeDraw: (chart: any) => {},
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}

        {/* Table Container */}
        <div className="glass-card p-4 sm:p-6">
          <DataTable
            ref={dt}
            value={data}
            paginator
            rows={isMobile ? 5 : 10}
            // rowsPerPageOptions={[5, 10, 25, 50]}
            stripedRows
            responsiveLayout="scroll"
            className="custom-datatable"
            header={header}
            emptyMessage={t('predictionAccuracy.noDataAvailable')}
            sortField="accuracyPercentage"
            sortOrder={-1}
            style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}
            removableSort
          >
            <Column
              field="accuracyPercentage"
              header={t('predictionAccuracy.accuracy')}
              body={accuracyBodyTemplate}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="predictionType"
              header={t('predictionAccuracy.type')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="league"
              header={t('predictionAccuracy.league')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="country"
              header={t('predictionAccuracy.country')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="seasonYear"
              header={t('predictionAccuracy.season')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="totalPredictions"
              header={t('predictionAccuracy.total')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
            <Column
              field="correctPredictions"
              header={t('predictionAccuracy.correct')}
              sortable
              className="centered-column"
              headerClassName="centered-header"
              style={{ textAlign: 'center' }}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default PredictionAccuracyCharts;
